import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const basicPromiseCode = `// Creating a basic Promise representing a future value
const promise = new Promise((resolve, reject) => {
    console.log("Executor runs immediately and synchronously!");
    
    // Simulating an asynchronous network request
    setTimeout(() => {
        const success = true;
        
        if (success) {
            // Transitions state from PENDING to FULFILLED
            resolve('Operation successful!');
        } else {
            // Transitions state from PENDING to REJECTED
            reject(new Error('Operation failed!'));
        }
    }, 1000);
});

// Consuming the Promise via the then/catch API
promise
    .then(result => {
        console.log('Success handler triggered:', result);
    })
    .catch(error => {
        console.error('Failure handler triggered:', error.message);
    })
    .finally(() => {
        console.log('Cleanup logic executed regardless of outcome.');
    });`;

const constructorSyntaxCode = `// The Promise constructor pattern for wrapping non-promise APIs
function delay(ms) {
    return new Promise((resolve) => {
        // resolve is passed as the callback to setTimeout
        setTimeout(resolve, ms);
    });
}

// Utilizing the delay utility to wait 2 seconds
delay(2000).then(() => {
    console.log('Control returned after 2 seconds.');
});

// A more complex example wrapping a legacy simulated API
function fetchUserMetadata(userId) {
    return new Promise((resolve, reject) => {
        if (!userId) {
            return reject(new Error("Missing User ID"));
        }

        setTimeout(() => {
            // Successfully resolving with a data object
            resolve({
                id: userId,
                role: 'Engineer',
                lastLogin: new Date().toISOString()
            });
        }, 800);
    });
}`;

const promiseChainingCode = `// Demonstrating the power of linear composition through chaining
fetchUserAccount(123)
    .then(user => {
        console.log('Step 1: User fetched', user.name);
        // Returning a new promise to keep the chain flat
        return fetchUserPermissions(user.id);
    })
    .then(permissions => {
        console.log('Step 2: Permissions retrieved', permissions.length);
        // Transforming the data; this value is wrapped in a resolved promise
        return permissions.filter(p => p.active);
    })
    .then(activePermissions => {
        console.log('Step 3: Processed active permissions', activePermissions);
    })
    .catch(error => {
        // Universal error handler for any failure in the chain above
        console.error('Chain broken by error:', error.message);
    });`;

const staticMethodsCode = `// Standardizing multiple async operations with static methods

// 1. Promise.all: All must succeed, or it fails fast
Promise.all([
    fetch('/api/v1/config'),
    fetch('/api/v1/theme'),
    fetch('/api/v1/locale')
]).then(([config, theme, locale]) => {
    console.log('Full application environment loaded.');
});

// 2. Promise.race: First one to settle wins (used for timeouts)
const timeout = (ms) => new Promise((_, r) => setTimeout(() => r('Timeout'), ms));
Promise.race([
    fetch('/api/slow-service'),
    timeout(2000)
]).catch(err => console.log('Operation timed out or failed.'));

// 3. Promise.allSettled: Waits for everything, never fails the group
Promise.allSettled([
    uploadImage(file1),
    uploadImage(file2)
]).then(results => {
    const failed = results.filter(r => r.status === 'rejected');
    console.log(\`Uploaded \${results.length - failed.length} files.\`);
});

// 4. Promise.any: First one to fulfill wins; ignores rejections
Promise.any([
    pingServer('US-East'),
    pingServer('EU-West')
]).then(fastestResponse => {
    console.log('Connected to fastest healthy node.');
});`;

const JSPromises = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Asynchronous JavaScript</div>
                <h1>Promises & The Future Value Pattern</h1>
                <p className="hero-lead">
                    Move beyond callbacks into the world of Promises. Learn how this architectural pattern
                    standardizes asynchronous control flow, solves trust issues, and enables clean,
                    linear reasoning about time-dependent logic.
                </p>
            </div>

            <div className="content-section">
                <h2>The Promise Architecture: A Quest for Trust</h2>
                <p>
                    A Promise is more than just a replacement for callbacks; it is a standardized container for a
                    value that hasn't arrived yeta "future value." In the old callback model, we faced
                    the "Inversion of Control" problem, where we handed our code to a third-party library and
                    hoped it would call our callback correctly. Promises flip this dynamic by returning a
                    state-managed object to us, restoring control to our main execution thread. This architectural
                    shift ensures that our success or failure handlers are executed exactly once and always
                    asynchronously. By providing a consistent API for representing completion or failure,
                    Promises allow developers to reason about asynchronous code with the same mental model used for
                    synchronous return values.
                </p>
                <p>
                    The life of a Promise is defined by its internal state, which is immutable once settled.
                    Every Promise begins in the <strong>Pending</strong> state, indicating the operation hasn't
                    concluded. From there, it can transition to <strong>Fulfilled</strong> (success) with a value,
                    or to <strong>Rejected</strong> (failure) with a reason, typically an Error object. Once a
                    Promise reaches either of these states, it is considered <strong>Settled</strong>. An
                    essential guarantee of the Promise specification is that a settled Promise cannot change its
                    state or its resolved value ever again. This immutability is what provides the "trust" that
                    was missing in raw callbacks, preventing scenarios where a callback might be triggered
                    multiple times unexpectedly.
                </p>

                <ProTip><ul>
                        <li><strong>Pending:</strong> The default state; the asynchronous task is still in progress and the outcome is unknown.</li>
                        <li><strong>Fulfilled:</strong> The task completed successfully, and the Promise now holds the resulting value.</li>
                        <li><strong>Rejected:</strong> The task encountered an error, and the Promise holds the reason for that failure.</li>
                        <li><strong>Settled:</strong> A non-spec term meaning the Promise is either Fulfilled or Rejected and will never change again.</li>
                    </ul></ProTip>

                <CodeBlock code={basicPromiseCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Microtask Queue: Why Promises are Special</h2>
                <p>
                    One of the most critical aspects of Promises from a performance and execution standpoint is how
                    they interact with the JavaScript Event Loop. Unlike `setTimeout` or DOM events which use the
                    standard **Task Queue** (or Macrotask Queue), Promises utilize the **Microtask Queue**. After
                    the current synchronous execution finishes, the engine checks the Microtask Queue and
                    drains it completely before moving on to the next Macrotask. This means that Promise handlers
                    residing in `.then()` will always execute before the next paint cycle or the next timer
                    callback. This prioritization ensures that state updates triggered by Promises happen
                    as quickly as possible, maintaining a high level of application responsiveness. Understanding
                    this distinction is vital for debugging complex race conditions and optimizing rendering
                    performance in modern web applications.
                </p>
                <p>
                    When you create a Promise using the `new Promise()` constructor, the code inside the executor
                    function actually runs **synchronously** and immediately. This is a common point of confusion;
                    the *asynchronicity* only begins when we hit the trigger (like `setTimeout` or a network
                    request) and when we schedule the result handlers. The executor facilitates the bridge
                    between synchronous setup and asynchronous resolution. This design allows you to perform
                    initial setup stepslike showing a loading spinnerexactly when the Promise is instantiated.
                    The resolve and reject functions provided to the executor are the "knobs" that transition
                    the Promise out of its pending state. By wrapping legacy callback-based APIs in this
                    constructor, you "Promisify" the operation, bringing it into the modern, trustworthy
                    ecosystem of the Promise API.
                </p>

                <CodeBlock code={constructorSyntaxCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Promise Chaining & Fluent Composition</h2>
                <p>
                    The true elegance of Promises shines during **Composition**, where multiple operations are
                    strung together to form a complex workflow. Every call to `.then()` returns a brand new
                    Promise, regardless of what the handler itself returns. If you return a simple value (like
                    a string or a number), the new Promise resolves to that value immediately. If you return
                    another Promise, the chain "pauses" and waits for that inner Promise to settle before
                    continuing. This capability allows us to flatten what would have been a deep "Pyramid of Doom"
                    into a clean, top-to-bottom list of instructions. It transforms nested logic into a
                    fluent interface that mirrors the sequence of steps in our business logic, making the
                    code self-documenting and significantly easier to audit for bugs.
                </p>
                <p>
                    Error handling in chains is equally standardized, following a mechanism similar to synchronous
                    exceptions. If any step in a Promise chain throws an error or returns a rejected Promise,
                    execution skips all subsequent `.then()` handlers and jumps to the nearest `.catch()`.
                    This "bubbling" of errors means you don't have to write manual error checks at every single
                    level of your operation as you do with callbacks. You can place a single `.catch()` at the end
                    of a chain to handle any failure that might have occurred in the preceding five or ten steps.
                    Furthermore, if you need to perform cleanup actionslike closing a database connection or
                    hiding a modalthe `.finally()` method provides a dedicated block that runs regardless of
                    whether the chain succeeded or failed. This structural symmetry between success and failure
                    paths drastically reduces the "boilerplate" code required for robust application logic.
                </p>

                <CodeBlock code={promiseChainingCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Static Powerhouse: Managing Multiple Promises</h2>
                <p>
                    While individual Promises are useful, real-world applications often need to manage groups of
                    asynchronous tasks simultaneously. The static methods on the `Promise` object provide high-level
                    concurrency primitives that would be incredibly difficult to implement manually with callbacks.
                    `Promise.all` is the "heavy lifter," allowing you to kick off multiple operations in parallel
                    and wait for all of them to finish before proceeding. However, it is "fail-fast," meaning if
                    even one of the operations fails, the entire group is rejected immediately. This makes it
                    ideal for scenarios where you need a complete set of datalike a user's profile, settings,
                    and notificationsbefore you can render the dashboard. By running these tasks in parallel
                    rather than sequentially, you significantly reduce the total "wait time" for your users.
                </p>
                <p>
                    Other static methods offer different strategies for handling concurrency and failures.
                    `Promise.allSettled` is safer than `all` because it waits for every task to finish regardless
                    of outcome, providing an array of status objects that detail exactly which tasks succeeded
                    and which failed. This is perfect for bulk operations, like uploading ten images and
                    reporting back to the user that only eight were successful. `Promise.race`, on the other
                    hand, is excellent for implementing timeouts; you can race your actual network request
                    against a timer Promise that rejects after five seconds. Finally, `Promise.any` is useful
                    for redundancy, where you might query three different servers for the same data and only
                    care about the "first successful" response. These primitives give developers a toolbox for
                    building highly resilient and performant distributed systems right within the browser.
                </p>

                <CodeBlock code={staticMethodsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Engineering Best Practices</h2>
                <p>
                    To maintain a healthy codebase, always prefer returning Promises from functions rather than
                    using internal callbacks or global state. This makes your functions "composable" and allows
                    callers to decide how they want to handle the results or errors. Avoid the "explicit construction
                    anti-pattern," which involves creating a new Promise manually when you already have one
                    available; instead, leverage the return values of `.then()` to keep your chains clean.
                    Always include a `.catch()` unless you are specifically returning the Promise to a parent
                    function that will handle the error. Forgetting to catch rejections can lead to memory leaks
                    and "unhandled rejections," which in modern environments can crash your process or clutter
                    your logs with cryptic messages. By following these patterns, you ensure that your
                    asynchronous logic remains predictable, testable, and robust against the unpredictability
                    of the network.
                </p>

                <KeyFacts><ul>
                        <li>**Standardization:** Resolves the "Inversion of Control" trust issue.</li>
                        <li>**Priority:** Promise callbacks execute in the Microtask Queue.</li>
                        <li>**Composition:** Chains remain flat via return values in `.then()`.</li>
                        <li>**Robustness:** Errors propagate automatically to the nearest `.catch()`.</li>
                        <li>**Concurrency:** Use static methods like `Promise.all` for parallel execution.</li>
                        <li>**Safety:** Once settled, a Promise's value and state are immutable.</li>
                        <li>**Cleanliness:** Use `.finally()` for logic that must run regardless of outcome.</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default JSPromises;



